<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useADialog } from '@Atoms';
import { capitalizeStr } from '@Helpers';
import { useApplicationsStatus } from '@AdoptionModule';

const emits = defineEmits<{
  blur: [];
}>();

const props = defineProps<{
  formType?: 'foster';
  isFormsAndApplications?: boolean;
  isEditingOnCell?: boolean;
  loading?: boolean;
  rowInfo?: any;
}>();

const { filterLockedOptions, onOpenAdoptApplicationStatusModal, onOpenFosterApplicationStatusModal, useOptions } =
  useApplicationsStatus();
const { activeModal, activeModalProps, dialogVisibility, SYSTEM_MODALS } = useADialog();

const ALL_OPTIONS = useOptions(props.formType ?? !!props.isFormsAndApplications);

const _initialModelValue = ref(props.rowInfo.status);
const _model = ref(props.rowInfo.status);

// The adopter already holds another ACTIVE application, so this dead one must not come back to life —
// api rejects that write too. It can still move between inactive statuses, so keep the dropdown and
// offer only those, with a tooltip saying why the rest are missing.
const isStatusLocked = computed(() => !!props.rowInfo?.is_status_locked);
const options = computed(() => filterLockedOptions(ALL_OPTIONS, isStatusLocked.value));

const applicationId = computed(() => {
  return props.isFormsAndApplications || props.formType
    ? props.rowInfo?._id?.$oid
    : props.rowInfo?.application_id?.$oid;
});

watch(applicationId, () => (_model.value = props.rowInfo.status));

function openModal() {
  if (!props.isFormsAndApplications && !props.formType) {
    onOpenAdoptApplicationStatusModal(props.rowInfo, _model.value);
    emits('blur');
    return;
  }

  const checkModel = !_model.value || _model.value === _initialModelValue.value;
  if (checkModel) return;

  const { status, _id, form_type, people_id } = props.rowInfo;
  const newStatus = _model.value?.toLowerCase?.() ?? '';
  const oldStatus = status?.toLowerCase?.() ?? '';
  const isDoNotFosterChange =
    props.formType === 'foster' && (newStatus === 'do not foster' || oldStatus === 'do not foster');

  if (isDoNotFosterChange) {
    onOpenFosterApplicationStatusModal(props.rowInfo, _model.value);
    emits('blur');
    return;
  }

  const modalProps = {
    applicationId: _id.$oid,
    formType: form_type,
    oldStatus: status,
    peopleId: people_id?.$oid || people_id,
    status: _model.value,
  };

  activeModalProps.value = modalProps;
  activeModal.value = SYSTEM_MODALS.INDIVIDUAL_ADOPTION_APPLICATIONS;
  dialogVisibility.value = true;
  emits('blur');
}
</script>

<template>
  <div class="flex items-center gap-1">
    <Dropdown
      v-model="_model"
      showClear
      class="!h-9 !w-fit min-w-fit"
      optionLabel="label"
      optionValue="value"
      pt:input="flex items-center py-0.5 pr-0 pl-1 !text-xs text-gray-500 font-medium"
      pt:item="!overflow-x-hidden !text-sm"
      pt:list="!w-fit !max-w-none !px-0"
      :disabled="loading"
      :options
      :placeholder="$t('filters.changeApplicationStatus.title')"
      :virtualScrollerOptions="{ itemSize: 36 }"
      @change="openModal"
    >
      <template #clearicon>
        <Button
          outlined
          class="af-button ml-1 h-4 w-4 border-none p-0 !text-slate-400 hover:bg-transparent"
          icon="pi pi-times"
          pt:icon="text-sm"
          @click="emits('blur')"
        />
      </template>

      <template #option="{ option }">
        <label class="cursor-pointer text-sm">{{ $t(option.label) }}</label>
      </template>

      <template #value="{ value }">
        <label class="cursor-pointer text-xs">{{ capitalizeStr(value) }}</label>
      </template>
    </Dropdown>

    <i
      v-if="isStatusLocked"
      v-tooltip.top="$t('adoption.oneActiveApplicationHint')"
      class="pi pi-info-circle cursor-help text-xs text-gray-400"
    ></i>
  </div>
</template>
